import { MutationFunction } from "@apollo/client";
import { AnchorButton, Button, LoadingIndicator } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import Link from "next/link";
import React from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import {
  IChangeSubscriptionPlanMutation,
  IChangeSubscriptionPlanMutationVariables,
} from "~/lib/mutations/__generated__/ChangeSubscriptionPlan.generated";
import { IStripePlanFieldsFragment } from "~/lib/queries/fragments/__generated__/StripePlanFields.generated";
import { IPlansQueryQuery } from "~/lib/queries/__generated__/PlansQuery.generated";
import { IPlanLimits, IPlanTier } from "~/types/types";
import mediaQueries from "~/components/mediaQueries";
import ChangePlanOption from "~/components/makers/ChangePlanOption";
import ConfirmPlanChange from "~/components/makers/ConfirmPlanChange";

interface IChangePlanOptionsProps {
  vendorSlug: string;
  basicPlan: IPlansQueryQuery["plans"][0];
  selectedPlanToChange: string | null;
  setSelectedPlanToChange: (arg: string) => void;
  stripePlans: IStripePlanFieldsFragment[];
  currentPlan: IStripePlanFieldsFragment;
  changeSubscriptionPlan: MutationFunction<IChangeSubscriptionPlanMutation, IChangeSubscriptionPlanMutationVariables>;
  isChangingPlans: boolean;
  moveToChangeStep: React.Dispatch<
    React.SetStateAction<"COMPARE_BOTH_PLANS" | "CONFIRM_CHANGES" | "MEET_REQUIREMENTS" | "TYPE_DOWNGRADE" | null>
  >;
}

export default function ChangePlanOptions({
  vendorSlug,
  basicPlan,
  selectedPlanToChange,
  setSelectedPlanToChange,
  stripePlans,
  currentPlan,
  changeSubscriptionPlan,
  isChangingPlans,
  moveToChangeStep,
}: IChangePlanOptionsProps) {
  const intl = useIntl();

  const linkToDetails = (
    <Link href="/makers" passHref={true} legacyBehavior={true}>
      <a
        css={{
          fontSize: 14,
          lineHeight: 1.33,
          color: palette.innovatorBlue,
        }}
      >
        <FormattedMessage defaultMessage="Details" id="Lv0zJu" />
      </a>
    </Link>
  );

  const changeToPlan = stripePlans.find((plan) => plan.planId === selectedPlanToChange);

  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 20,
      }}
    >
      <ChangePlanOptionsHeader vendorSlug={vendorSlug} />

      <div
        css={{
          display: "grid",
          gridRowGap: 20,
        }}
      >
        <ChangePlanOption
          name="Basic"
          description={
            basicPlan
              ? getPlanDescription(basicPlan.limits, basicPlan.tier as IPlanTier, intl)
              : "Basic product profiles."
          }
          price={0}
          viewerIsSubscribed={false}
          isSelected={selectedPlanToChange === "BASIC"}
          onSelect={() => setSelectedPlanToChange("BASIC")}
        />

        {stripePlans
          .sort((a, b) => PLANS_ORDER.indexOf(a.name) - PLANS_ORDER.indexOf(b.name))
          .map((plan) => {
            const isSelected =
              selectedPlanToChange !== null ? selectedPlanToChange === plan.planId : plan.viewerIsSubscribed;

            return (
              <ChangePlanOption
                key={plan.planId}
                name={plan.displayName}
                description={
                  <>
                    {getPlanDescription(plan.limits, plan.tier, intl)}. {linkToDetails}
                  </>
                }
                price={plan.price}
                recurrency={plan.recurrency}
                viewerIsSubscribed={plan.viewerIsSubscribed}
                isSelected={isSelected}
                onSelect={() => setSelectedPlanToChange(plan.planId)}
              />
            );
          })}
      </div>

      <div
        css={mediaQueries({
          justifyContent: "center",
          display: "grid",
          gridTemplateColumns: ["1fr", "1fr", "1fr", "120px 120px"],
          gridColumnGap: 20,
          gridRowGap: 20,
        })}
      >
        <ConfirmPlanChange
          toPlan={changeToPlan ? changeToPlan : null}
          onConfirm={() => {
            if (changeToPlan) {
              changeSubscriptionPlan({
                variables: {
                  stripePlanId: changeToPlan.planId,
                },
              });
            }
          }}
        >
          {({ onRequestConfirmation }) => (
            <Button
              color={
                isChangingPlans ||
                selectedPlanToChange === null ||
                selectedPlanToChange === currentPlan.planId ||
                (changeToPlan && changeToPlan.tier === IPlanTier.Makerspro)
                  ? palette.darkGray
                  : palette.innovatorBlue
              }
              disabled={
                selectedPlanToChange === null ||
                isChangingPlans ||
                selectedPlanToChange === currentPlan.planId ||
                (changeToPlan && changeToPlan.tier === IPlanTier.Makerspro)
              }
              onClick={() => {
                if (!selectedPlanToChange) {
                  return;
                }

                const isDowngrade =
                  selectedPlanToChange === "BASIC" || (changeToPlan && changeToPlan.price < currentPlan.price);

                // we'll prompt the user with some more confirmations before he could actually confirm the downgrade
                if (isDowngrade) {
                  moveToChangeStep("COMPARE_BOTH_PLANS");
                  return;
                }

                // it's an upgrade. We shouldn't ask for any further confirmation.
                if (selectedPlanToChange && selectedPlanToChange !== "BASIC" && changeToPlan) {
                  moveToChangeStep(null);
                  onRequestConfirmation();
                }
              }}
            >
              {isChangingPlans ? <LoadingIndicator /> : <FormattedMessage defaultMessage="Continue" id="acrOoz" />}
            </Button>
          )}
        </ConfirmPlanChange>

        <Link
          href="/vendors/[vendorSlug]/[[...content]]"
          as={`/vendors/${vendorSlug}/account`}
          passHref={true}
          legacyBehavior={true}
        >
          <div css={{ textAlign: "center" }}>
            <AnchorButton color={palette.darkGray} theme="secondary" disabled={isChangingPlans}>
              <FormattedMessage defaultMessage="Go Back" id="ekfOaV" />
            </AnchorButton>
          </div>
        </Link>
      </div>
    </div>
  );
}

interface IChangePlanOptionsHeaderProps {
  vendorSlug: string;
}

function ChangePlanOptionsHeader({ vendorSlug }: IChangePlanOptionsHeaderProps) {
  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 8,
      }}
    >
      <h2
        css={{
          fontSize: 26,
          color: palette.text,
          margin: 0,
          lineHeight: 1.33,
          fontWeight: "normal",
        }}
      >
        <FormattedMessage defaultMessage="Change Plans" id="D+Kmq3" />
      </h2>

      <Link
        href="/vendors/[vendorSlug]/[[...content]]"
        as={`/vendors/${vendorSlug}/account`}
        passHref={true}
        legacyBehavior={true}
      >
        <a
          css={{
            color: palette.innovatorBlue,
          }}
        >
          <FormattedMessage defaultMessage="Back to Account" id="diSXRh" />
        </a>
      </Link>
    </div>
  );
}

function getPlanDescription(
  limits: Omit<IPlanLimits, "enrichedProfile" | "inviteGovernments" | "searchAccess">,
  name: IPlanTier,
  intl: IntlShape,
) {
  const features: string[] = [];

  if (name === IPlanTier.Makerspro) {
    features.push(
      intl.formatMessage({
        defaultMessage: "For companies with greater than 20 employees",
        id: "z7MBFW",
      }),
    );
    return features.join(" ");
  }

  if (limits.enrichedProducts > 0) {
    features.push(
      intl.formatMessage(
        {
          defaultMessage: "Enhanced profiles for {count} products.",
          id: "9K+IvJ",
        },
        { count: limits.enrichedProducts },
      ),
    );
  } else {
    features.push(
      intl
        .formatMessage({
          defaultMessage: "Basic product profiles",
          id: "AcV6wk",
        })
        .concat("."),
    );
  }

  if (limits.projectsPerMonth > 0) {
    features.push(
      intl.formatMessage(
        {
          defaultMessage: "Add up to {projectsPerMonth, plural, one {# project} other {# projects}} per month",
          id: "BdE/Lt",
        },
        { projectsPerMonth: limits.projectsPerMonth },
      ),
    );
  }

  if (limits.teamMembers > 0) {
    features.push(
      intl.formatMessage(
        {
          defaultMessage: "{count} team seats.",
          id: "lsftOu",
        },
        { count: limits.teamMembers },
      ),
    );
  }

  if (limits.groups > 0) {
    features.push(
      intl.formatMessage(
        {
          defaultMessage: "{count} sponsored groups.",
          id: "RjfhhG",
        },
        { count: limits.groups },
      ),
    );
  }

  if (limits.sharingPage) {
    features.push(
      intl.formatMessage({
        defaultMessage: "Custom share page.",
        id: "CLqXQG",
      }),
    );
  }

  return features.join(" ");
}

const PLANS_ORDER = ["Basic", "Makers", "Makers Pro"];
